<template>
  <div class="py-12 space-y-6">
    <!-- Header -->
    <div class="rounded-2xl border border-gray-200 bg-white p-6">
      <div class="flex flex-col sm:flex-row gap-6 items-start sm:items-center">
        <div class="w-24 h-24 rounded-xl overflow-hidden border bg-gray-50 flex-shrink-0">
          <ImgComponent
            :src="categoryData?.imageUrl"
            fallback="/images/logo.png"
            alt="Category"
            class="w-full h-full object-cover"
          />
        </div>
        <div class="flex-1">
          <h1 class="text-2xl font-bold title-color">{{ categoryData?.name }}</h1>
          <p class="text-gray-500 text-sm mt-1">ID: {{ categoryData?.id }}</p>
          <div class="mt-3 flex gap-2">
            <span v-if="categoryData?.active" class="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
              Actif
            </span>
            <span v-else class="px-3 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">
              Inactif
            </span>
          </div>
        </div>
        <div class="flex gap-2 w-full sm:w-auto mt-4 sm:mt-0">
          <button @click="isEditing = true" class="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors flex-1 sm:flex-initial">
            Modifier
          </button>
          <button @click="confirmDelete" class="px-4 py-2 bg-red-50 text-red-600 border border-red-100 rounded-lg hover:bg-red-100 transition-colors flex-1 sm:flex-initial">
            Supprimer
          </button>
        </div>
      </div>
    </div>

    <!-- Edit Form (v-if="isEditing") -->
    <div v-if="isEditing" class="rounded-2xl border border-gray-200 bg-white p-6 space-y-4">
      <h3 class="text-lg font-semibold text-gray-800">Modifier la catégorie</h3>
      <div class="grid grid-cols-1 gap-4">
        <div class="space-y-1">
          <label class="text-sm font-medium text-gray-700">Nom</label>
          <input v-model="form.name" type="text" class="w-full h-11 px-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary focus:border-transparent outline-none" />
        </div>
        <div class="space-y-1">
          <label class="text-sm font-medium text-gray-700">Image</label>
          <FileUploader
            placeHolder="Choisir une image pour la catégorie"
            :acceptedFilesTypes="['image/*']"
            @onFileChangedV2="handleImageUpload"
            :fileId="form.imageUrl"
          />
          <div v-if="form.imageUrl" class="mt-3">
            <img
              :src="form.imageUrl"
              alt="Preview"
              class="h-20 w-20 object-contain rounded-lg border"
            />
          </div>
        </div>
      </div>
      <div class="flex items-center gap-2 py-2">
        <input type="checkbox" v-model="form.active" id="edit-active" class="w-4 h-4 text-primary rounded border-gray-300" />
        <label for="edit-active" class="text-sm text-gray-700">Catégorie active</label>
      </div>
      <div class="flex gap-3 justify-end pt-4 border-t">
        <button @click="isEditing = false" class="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors">
          Annuler
        </button>
        <button @click="updateCategory" :disabled="loading" class="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors min-w-[100px]">
          <span v-if="loading" class="animate-spin inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2"></span>
          Enregistrer
        </button>
      </div>
    </div>

    <!-- Details -->
    <div class="rounded-2xl border border-gray-200 bg-white p-6">
      <h3 class="text-lg font-semibold text-gray-800 mb-4">Sous-catégories</h3>
      <div v-if="categoryData?.subcategories?.length > 0" class="flex flex-wrap gap-2">
        <span v-for="sub in categoryData.subcategories" :key="sub.id" class="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm border border-gray-200">
          {{ sub.name || sub }}
        </span>
      </div>
      <p v-else class="text-gray-500 italic">Aucune sous-catégorie définie.</p>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, watch } from 'vue'
import ImgComponent from '@/presentation/components/img/ImgComponent.vue'
import FileUploader from '@/presentation/components/input/FileUploader.vue'
import ApiService from '@/shared/utils/apiService'
import Events from '@/events/events'
import { EVENTS } from '@/shared/constants/commons'
import Modal from '@/shared/utils/modal'

const props = defineProps({
  categoryData: {
    type: Object,
    default: null
  }
})

const emit = defineEmits(['updated', 'deleted'])

const isEditing = ref(false)
const loading = ref(false)
const form = reactive({
  name: '',
  imageUrl: '',
  active: true
})

watch(() => props.categoryData, (newVal) => {
  if (newVal) {
    form.name = newVal.name || ''
    form.imageUrl = newVal.imageUrl || ''
    form.active = newVal.active !== undefined ? newVal.active : true
  }
}, { immediate: true })

async function handleImageUpload(fileData) {
  form.imageUrl = fileData.url
}

async function updateCategory() {
  if (!form.name) return Modal.show('Erreur', 'Le nom est obligatoire')

  loading.value = true
  try {
    form.iconUrl = form.imageUrl
    const res = await ApiService.post(`categories/update/${props.categoryData.id}?group=ultraxeno`, form)
    if (res.success) {
      isEditing.value = false
      Events.dispatch(EVENTS.NOTIFICATION, {
        type: 'success',
        message: 'Catégorie mise à jour avec succès'
      })
      emit('updated')
    }
  } catch (err) {
    Modal.show('Erreur', err.message || 'La mise à jour a échoué')
  } finally {
    loading.value = false
  }
}

async function confirmDelete() {
  const confirm = await Modal.confirm('Supprimer', `Voulez-vous vraiment supprimer la catégorie "${props.categoryData.name}" ?`)
  if (confirm) {
    try {
      await ApiService.post(`categories/delete/${props.categoryData.id}?group=ultraxeno`)
      Events.dispatch(EVENTS.NOTIFICATION, {
        type: 'success',
        message: 'Catégorie supprimée'
      })
      emit('deleted')
    } catch (err) {
      Modal.show('Erreur', err.message || 'La suppression a échoué')
    }
  }
}
</script>
